import ZtServiceFactory from '../zt';
const INVALID_CONTEXT_MESSAGE = 'invalid activity context';

function mapActivityStage(status) {
  switch (status) {
    case 0:
      return 'notStarted';
    case 1:
      return 'active';
    case 2:
      return 'ended';
    default:
      return null;
  }
}

function mapPlayerType(type) {
  switch (type) {
    case 0:
      return 'unknown';
    case 1:
      return 'new';
    case 2:
      return 'veteran';
    default:
      return null;
  }
}

/**
 * Convert the raw get_config status fields into the shared activity ViewModel.
 * team_status and other response fields intentionally stay outside the shared session boundary.
 *
 * @param {object} rawContext Raw data returned by get_config.
 * @returns {{activityStage:string, playerType:string, account:string, shareCode:string, reviewStatus:number}} Stable context consumed by page stores.
 */
function normalizeActivityContext(rawContext) {
  if (!rawContext || typeof rawContext.activity_status !== 'number' || typeof rawContext.player_type !== 'number') {
    throw new Error(INVALID_CONTEXT_MESSAGE);
  }

  const activityStage = mapActivityStage(rawContext.activity_status);
  const playerType = mapPlayerType(rawContext.player_type);

  if (!activityStage || !playerType) {
    throw new Error(INVALID_CONTEXT_MESSAGE);
  }

  // account 为服务端脱敏通行证（未登录返回空字符串）：右上角登录态的服务端视角数据源
  // shareCode 为当前用户个人分享码：组队转发客态链接的归因参数（后端据此标记 is_inviter）
  // reviewStatus 为回顾完成事实（0 未完成 / 1 已完成）：底栏组队域门控的共享判断依据（2026-09-20 需求），
  // 缺失或非法值按未完成兜底，避免异常响应放大门控口径
  return {
    activityStage,
    playerType,
    account: typeof rawContext.account === 'string' ? rawContext.account : '',
    shareCode: typeof rawContext.share_code === 'string' ? rawContext.share_code : '',
    reviewStatus: rawContext.review_status === 1 ? 1 : 0
  };
}

/**
 * Request the activity context with the current login state.
 * Share parameters are passed only when the caller already has explicit entry context.
 *
 * @param {object} options Provider injection and optional entry context.
 * @param {Function} options.provider Test-only provider override returning raw get_config data.
 * @param {string} options.entrySource Known activity entry source.
 * @param {string} options.fromTeamId Known source team ID.
 * @param {string} options.shareCode Known inviter share code.
 * @returns {Promise<{activityStage:string, playerType:string}>} Shared activity ViewModel.
 */
export function loadActivityContext(options = {}) {
  const provider =
    options.provider || (() => ZtServiceFactory.getDefaultInstance().getConfig(undefined, options.entrySource, options.fromTeamId, options.shareCode));

  return Promise.resolve()
    .then(() => provider(options))
    .then(normalizeActivityContext);
}

export default {
  loadActivityContext
};
